import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import moment from "moment";

Font.register({
  family: "Tajawal",
  src: "/fonts/Tajawal-Regular.ttf",
  productImage: {
    width: 50,
    height: 50,
    marginRight: 5,
  },
});
interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
  discount: number;
  notice: string;
  image: string;
  attachment: string;
}
interface Company {
  id: string;
  nameAr: string;
  nameEn: string;
  address: string;
  region: string;
  city: string;
  contactInfo: string;
  CompanyNr: string;
}

interface Tender {
  id: string;
  title: string;
  minValue: number;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  city: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  company: Company;
  Buycompany: Company;
  Tender: Tender;
}

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Tajawal",
    flexDirection: "column",
    backgroundColor: "#F9F9F9",
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: "center",
    color: "#333",
  },
  section: {
    marginBottom: 10,
    padding: 10,
    borderBottom: "1px solid #CCC",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    textAlign: "right",
    color: "#007BFF",
  },
  sectionText: {
    fontSize: 12,
    marginBottom: 5,
    textAlign: "right",
    color: "#555",
  },
  table: {
    flexDirection: "column",
    width: "100%",
    marginVertical: 10,
    borderCollapse: "collapse",
  },
  tableRow: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#CCC",
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    padding: 5,
    textAlign: "right",
    color: "#333",
  },
  row: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginBottom: 5,
  },
  productImage: {
    width: 50,
    height: 50,
    margin: "auto",
  },
});

const OrderPDF = ({ order }: { order: any }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>{t("orders.details")}</Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t("orders.infos")}</Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>:{t("orders.number")} </Text>
            <Text style={styles.sectionText}>{order.OrderNR}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.sectionText}>:{t("orders.title1")} </Text>
            <Text style={styles.sectionText}>{order.title}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("company.fields.address")}{" "}
            </Text>
            <Text style={styles.sectionText}>{order.address || "N/A"}</Text>
          </View>
          <Text style={styles.sectionText}>
            {t("orders.fields.deliveryDate")}:{" "}
            {moment(order.deliverDate).format("YYYY-MM-DD")}
          </Text>
          <Text style={styles.sectionText}>
            {order.products
              .reduce(
                (sum: number, p: Product) => sum + p.price * p.quantity,
                0
              )
              .toFixed(2)}{" "}
            {t("checkout.summary.currency")}
          </Text>
        </View>
        {/* Company Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {t("orders.buyerCompanyDetails")}
          </Text>
          <Text style={styles.sectionText}>
            {t("item.fields.name")}: {order.Buycompany.nameAr}
          </Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("company.fields.address")}{" "}
            </Text>
            <Text style={styles.sectionText}>{order.Buycompany.address}</Text>
          </View>
          <Text style={styles.sectionText}>
            {t("company.fields.region")}: {order.Buycompany.region}
          </Text>
          <Text style={styles.sectionText}>
            {t("company.fields.city")}: {order.Buycompany.city}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {t("orders.supplierCompanyDetails")}
          </Text>
          <Text style={styles.sectionText}>
            {t("item.fields.name")}: {order.company.nameAr}
          </Text>
          <View style={styles.row}>
            <Text style={styles.sectionText}>
              :{t("company.fields.address")}{" "}
            </Text>
            <Text style={styles.sectionText}>{order.company.address}</Text>
          </View>
          <Text style={styles.sectionText}>
            {t("company.fields.region")}: {order.company.region}
          </Text>
          <Text style={styles.sectionText}>
            {t("company.fields.city")}: {order.company.city}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t("products.title")}</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}></Text>
              <Text style={styles.tableCell}>{t("products.name")}</Text>
              <Text style={styles.tableCell}>
                {t("checkout.fields.quantity")}
              </Text>
              <Text style={styles.tableCell}>{t("item.fields.price")}</Text>
              <Text style={styles.tableCell}>
                {t("orders.fields.discount")}
              </Text>
            </View>
            {order.products.map((product: any) => (
              <View key={product.itemId} style={styles.tableRow}>
                <Image src={product.image || ""} style={styles.productImage} />
                <Text style={styles.tableCell}>{product.item}</Text>
                <Text style={styles.tableCell}>{product.quantity}</Text>
                <Text style={styles.tableCell}>
                  {product.price} {t("checkout.summary.currency")}
                </Text>
                <Text style={styles.tableCell}>
                  {product.discount} {t("checkout.summary.currency")}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};
const convertImageToBase64 = async (url: string): Promise<string> => {
  const placeholder = "data:image/png;base64,...";
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(xhr.response);
    };
    xhr.onerror = () => resolve(placeholder);
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.send();
  });
};
export const generatePDFOrder = async (order: Order) => {
  // Convert all images to Base64
  const productsWithBase64Images = await Promise.all(
    order.products.map(async (product) => ({
      ...product,
      image: product.image ? await convertImageToBase64(product.image) : "",
    }))
  );
  const updatedOrder = { ...order, products: productsWithBase64Images };
  const blob = await pdf(<OrderPDF order={updatedOrder} />).toBlob();
  const url = URL.createObjectURL(blob);

  // Trigger the download
  const link = document.createElement("a");
  link.href = url;
  link.download = "tender-info.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
